import { useState, useEffect, useRef, useCallback } from 'react';
import { apiService } from '../services/api';
import toast from 'react-hot-toast';

interface Stage {
  name: string;
  status: 'pending' | 'running' | 'done' | 'error';
  detail: string;
  timestamp?: number;
}

interface MediaSent {
  name: string;
  url: string;
  mimeType: string;
  type: string;
  description: string;
  mensagem: string;
}

interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  stages?: Stage[];
  debug?: any;
  labeler?: any;
  activeCategoryId?: string | null;
  activeCategoryName?: string | null;
  mediaSent?: MediaSent | null;
}

interface CategoryOption {
  id: string;
  nome: string;
  descricao: string;
  prompt: string;
  agentMainObjective: string;
  visibleToLabeler: boolean;
  funnelOrder: number | null;
  funnel: { id: string; nome: string } | null;
}

const STAGE_LABELS: Record<string, string> = {
  MESSAGE_INPUT: 'Entrada',
  DEBOUNCE: 'Debounce',
  CHAT_MEMORY: 'Memoria',
  FETCH_PROMPT: 'Prompt',
  VECTOR_STORE_AGENT: 'Base Vetorial',
  FETCH_MEDIA: 'Midias',
  FETCH_HUMANS: 'Humanos',
  MAIN_AGENT: 'Agente Principal',
  SPLIT_MESSAGES: 'Split Msg',
  MEMORY_LONG: 'Memoria Longa',
  LABELER_AGENT: 'Etiquetador',
  COMPLETE: 'Completo',
};

function StageIcon({ status }: { status: string }) {
  if (status === 'done') return (
    <svg className="w-3.5 h-3.5 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
    </svg>
  );
  if (status === 'running') return (
    <svg className="w-3.5 h-3.5 text-gray-400 animate-spin" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
  );
  if (status === 'error') return (
    <svg className="w-3.5 h-3.5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
    </svg>
  );
  return <div className="w-3.5 h-3.5 rounded-full border-2 border-gray-600" />;
}

function compactStages(stages: Stage[] = []) {
  const byName = new Map<string, Stage>();
  const order: string[] = [];
  for (const stage of stages) {
    if (!byName.has(stage.name)) order.push(stage.name);
    byName.set(stage.name, stage);
  }
  return order.map((name) => byName.get(name)).filter(Boolean) as Stage[];
}

// Funnel category pipeline — shows all categories of the funnel with active one highlighted
function FunnelCategoryPipeline({
  categories,
  activeCategoryId,
  isProcessing,
}: {
  categories: CategoryOption[];
  activeCategoryId: string | null;
  isProcessing: boolean;
}) {
  if (categories.length === 0) return null;

  // Group by funnel
  const grouped = categories.reduce<Record<string, { funnelName: string; cats: CategoryOption[] }>>((acc, cat) => {
    const fid = cat.funnel?.id || '__none__';
    const fname = cat.funnel?.nome || 'Sem Funil';
    if (!acc[fid]) acc[fid] = { funnelName: fname, cats: [] };
    acc[fid].cats.push(cat);
    return acc;
  }, {});

  return (
    <div className="space-y-2">
      {Object.entries(grouped).map(([fid, { funnelName, cats }]) => (
        <div key={fid}>
          <p className="text-[9px] uppercase tracking-widest text-gray-600 mb-1 font-semibold">{funnelName}</p>
          <div className="flex flex-wrap gap-1.5 items-center">
            {cats.map((cat, i) => {
              const isActive = cat.id === activeCategoryId;
              const isProcessingThis = isProcessing && isActive;
              return (
                <div key={cat.id} className="flex items-center gap-1">
                  <div
                    title={cat.descricao || cat.nome}
                    className={`
                      relative flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all duration-300
                      ${isActive
                        ? 'bg-gray-700 text-white border border-gray-600'
                        : 'bg-gray-800/60 text-gray-500 border border-gray-700/50 hover:text-gray-400'}
                    `}
                  >
                    {isProcessingThis ? (
                      <svg className="w-3 h-3 text-white animate-spin flex-shrink-0" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                    ) : isActive ? (
                      <div className="w-2 h-2 rounded-full bg-white flex-shrink-0 animate-pulse" />
                    ) : (
                      <div className="w-2 h-2 rounded-full bg-gray-600 flex-shrink-0" />
                    )}
                    <span className="truncate max-w-[100px]">{cat.nome}</span>
                  </div>
                  {i < cats.length - 1 && (
                    <svg className="w-3 h-3 text-gray-700 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function MediaBubble({ media }: { media: MediaSent }) {
  const mime = media.mimeType?.toLowerCase() || '';
  const type = media.type?.toLowerCase() || '';
  const isImage = mime.startsWith('image/') || type === 'image';
  const isVideo = mime.startsWith('video/') || type === 'video';
  const isAudio = mime.startsWith('audio/') || type === 'audio';

  return (
    <div className="mt-3 rounded-xl overflow-hidden border border-gray-600 bg-gray-900">
      {isImage && (
        <a href={media.url} target="_blank" rel="noreferrer">
          <img
            src={media.url}
            alt={media.name || 'imagem'}
            className="max-w-full max-h-64 object-contain w-full"
            onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
        </a>
      )}
      {isVideo && (
        <video
          src={media.url}
          controls
          className="max-w-full max-h-48 w-full"
        />
      )}
      {isAudio && (
        <div className="p-3">
          <audio src={media.url} controls className="w-full" />
        </div>
      )}
      {!isImage && !isVideo && !isAudio && (
        <a
          href={media.url}
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-3 p-3 hover:bg-gray-800 transition-colors"
        >
          <div className="w-9 h-9 rounded-lg bg-gray-700 flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div className="min-w-0">
            <p className="text-sm text-gray-200 truncate font-medium">{media.name}</p>
            {media.description && (
              <p className="text-xs text-gray-500 truncate">{media.description}</p>
            )}
          </div>
          <svg className="w-4 h-4 text-gray-500 flex-shrink-0 ml-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
        </a>
      )}
      <div className="px-3 py-2 border-t border-gray-700 flex items-center gap-2">
        <svg className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        </svg>
        <span className="text-[11px] text-gray-400 truncate">
          Midia enviada: <span className="text-gray-200">{media.name}</span>
        </span>
      </div>
      {media.mensagem && (
        <div className="px-3 pb-2 text-xs text-gray-300">{media.mensagem}</div>
      )}
    </div>
  );
}

export function TestChatPage() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [skipLabeler, setSkipLabeler] = useState(false);
  const [sessionId] = useState(() => `test-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`);
  const [sessionInfo, setSessionInfo] = useState<any>(null);
  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);
  const [showDebug, setShowDebug] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    loadCategories();
  }, []);

  async function loadCategories() {
    try {
      const cats = await apiService.testChatCategories();
      setCategories(cats || []);
    } catch (err: any) {
      console.error('Failed to load categories:', err);
    }
  }

  async function handleSend() {
    const trimmed = input.trim();
    if (!trimmed || isProcessing) return;

    const userMsg: ChatMessage = {
      role: 'user',
      content: trimmed,
      timestamp: new Date(),
    };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsProcessing(true);

    try {
      const result = await apiService.testChatMessage({
        sessionId,
        message: trimmed,
        categoryId: selectedCategoryId || null,
        skipLabeler,
      });

      const newActiveCatId = result.session?.currentCategoryId || selectedCategoryId || null;
      setActiveCategoryId(newActiveCatId);

      if (result.success) {
        const assistantMsg: ChatMessage = {
          role: 'assistant',
          content: result.reply,
          timestamp: new Date(),
          stages: result.stages,
          debug: result.debug,
          labeler: result.labeler,
          activeCategoryId: newActiveCatId,
          activeCategoryName: result.session?.currentCategory || null,
          mediaSent: result.mediaSent || null,
        };
        setMessages(prev => [...prev, assistantMsg]);
        setSessionInfo(result.session);
      } else {
        const errorMsg: ChatMessage = {
          role: 'system',
          content: `Erro: ${result.error}`,
          timestamp: new Date(),
          stages: result.stages,
        };
        setMessages(prev => [...prev, errorMsg]);
        toast.error(result.error || 'Erro ao processar mensagem');
      }
    } catch (err: any) {
      const errorMsg: ChatMessage = {
        role: 'system',
        content: `Erro de conexao: ${err.message}`,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMsg]);
      toast.error(err.message || 'Erro de conexao');
    } finally {
      setIsProcessing(false);
      inputRef.current?.focus();
    }
  }

  async function handleSendAsAssistant() {
    const trimmed = input.trim();
    if (!trimmed || isProcessing) return;

    setIsProcessing(true);

    try {
      const result = await apiService.testChatAssistantMessage({
        sessionId,
        message: trimmed,
        categoryId: activeCategoryId || selectedCategoryId || null,
      });

      const newActiveCatId = result.session?.currentCategoryId || activeCategoryId || selectedCategoryId || null;
      setActiveCategoryId(newActiveCatId);
      setSessionInfo(result.session);
      setInput('');

      const assistantMsg: ChatMessage = {
        role: 'assistant',
        content: trimmed,
        timestamp: new Date(),
        activeCategoryId: newActiveCatId,
        activeCategoryName: result.session?.currentCategory || null,
      };

      setMessages(prev => [...prev, assistantMsg]);
      toast.success('Mensagem disparada como IA');
    } catch (err: any) {
      const errorMsg: ChatMessage = {
        role: 'system',
        content: `Erro de conexao: ${err.message}`,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMsg]);
      toast.error(err.message || 'Erro de conexao');
    } finally {
      setIsProcessing(false);
      inputRef.current?.focus();
    }
  }

  async function handleClear() {
    try {
      await apiService.testChatClear(sessionId);
      setMessages([]);
      setSessionInfo(null);
      setActiveCategoryId(selectedCategoryId || null);
      toast.success('Conversa limpa');
    } catch (err: any) {
      toast.error('Erro ao limpar conversa');
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  const selectedCategory = categories.find(c => c.id === selectedCategoryId);
  const groupedCategories = categories.reduce<Record<string, CategoryOption[]>>((acc, cat) => {
    const funnelName = cat.funnel?.nome || 'Sem Funil';
    if (!acc[funnelName]) acc[funnelName] = [];
    acc[funnelName].push(cat);
    return acc;
  }, {});

  // Categories of the selected funnel to show in pipeline
  const selectedFunnelId = selectedCategory?.funnel?.id || null;
  const funnelCategories = selectedFunnelId
    ? categories.filter(c => c.funnel?.id === selectedFunnelId)
    : categories;

  function getPromptSourceLabel(source?: string) {
    if (source === 'category') return 'Categoria';
    if (source === 'inbound') return 'Inbound';
    return 'Padrao';
  }

  return (
    <div className="h-screen flex flex-col bg-black">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-gray-800 bg-gray-900/80 backdrop-blur-sm">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-gray-800 flex items-center justify-center border border-gray-700">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
              </div>
              <div>
                <h1 className="text-base font-semibold text-white">Chat de Teste - Preview do Agente</h1>
                <p className="text-xs text-gray-400">Replica o fluxo completo de conversasão no Whatsapp</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowDebug(!showDebug)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${showDebug ? 'bg-gray-700 text-white border border-gray-600' : 'bg-gray-800 text-gray-400 border border-gray-700 hover:border-gray-600'}`}
              >
                Debug
              </button>
              <button
                onClick={handleClear}
                className="px-3 py-1.5 rounded-lg text-xs font-medium bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20 transition-all"
              >
                Limpar
              </button>
            </div>
          </div>

          {/* Category selector row */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 flex-1 min-w-[200px]">
              <label className="text-xs text-gray-400 whitespace-nowrap">Categoria inicial:</label>
              <select
                value={selectedCategoryId}
                onChange={e => {
                  setSelectedCategoryId(e.target.value);
                  setActiveCategoryId(e.target.value || null);
                }}
                className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-gray-500 transition-colors"
              >
                <option value="">-- Nenhuma (prompt padrao) --</option>
                {Object.entries(groupedCategories).map(([funnelName, cats]) => (
                  <optgroup key={funnelName} label={funnelName}>
                    {cats.map(cat => (
                      <option key={cat.id} value={cat.id}>
                        {cat.nome} {cat.prompt ? '' : '(sem prompt)'}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </div>

            <label className="flex items-center gap-1.5 text-xs text-gray-400 cursor-pointer">
              <input
                type="checkbox"
                checked={skipLabeler}
                onChange={e => setSkipLabeler(e.target.checked)}
                className="rounded border-gray-600 bg-gray-800 text-gray-400 focus:ring-gray-500"
              />
              Travar na Etiqueta
            </label>

            {selectedCategory?.prompt && (
              <button
                onClick={() => setShowPrompt(!showPrompt)}
                className="text-xs text-gray-400 hover:text-gray-300 transition-colors underline"
              >
                {showPrompt ? 'Esconder prompt' : 'Ver prompt'}
              </button>
            )}
          </div>

          {/* Show selected prompt */}
          {showPrompt && selectedCategory?.prompt && (
            <div className="mt-2 p-3 bg-gray-800/50 border border-gray-700 rounded-lg max-h-40 overflow-y-auto">
              <pre className="text-xs text-gray-300 whitespace-pre-wrap font-mono">{selectedCategory.prompt}</pre>
            </div>
          )}
        </div>

        {/* Funnel category pipeline panel */}
        {funnelCategories.length > 0 && (
          <div className="px-4 py-2.5 border-t border-gray-800/50 bg-gray-950/60 overflow-x-auto">
            <FunnelCategoryPipeline
              categories={funnelCategories}
              activeCategoryId={activeCategoryId}
              isProcessing={isProcessing}
            />
          </div>
        )}
      </div>

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="w-16 h-16 rounded-2xl bg-gray-800 flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </div>
            <h2 className="text-lg font-medium text-gray-300 mb-1">Chat de Teste do Funil</h2>
            <p className="text-sm text-gray-500 max-w-md">
              Selecione uma categoria inicial e envie uma mensagem. O etiquetador vai mover o lead automaticamente entre as categorias do funil.
            </p>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[75%] ${msg.role === 'system' ? 'w-full max-w-full' : ''}`}>
              {/* Message bubble */}
              <div
                className={`
                  rounded-2xl px-4 py-3 text-sm leading-relaxed
                  ${msg.role === 'user' ? 'bg-gray-700 text-white rounded-br-md' : ''}
                  ${msg.role === 'assistant' ? 'bg-gray-800 text-gray-200 border border-gray-700 rounded-bl-md' : ''}
                  ${msg.role === 'system' ? 'bg-red-500/10 text-red-300 border border-red-500/30 rounded-lg text-center' : ''}
                `}
              >
                <div className="whitespace-pre-wrap">{msg.content}</div>
                {/* Media sent by agent */}
                {msg.mediaSent && (
                  <MediaBubble media={msg.mediaSent} />
                )}
              </div>

              {/* Timestamp + active category for this message */}
              <div className={`mt-1 text-[10px] text-gray-600 ${msg.role === 'user' ? 'text-right' : 'flex items-center gap-2'}`}>
                <span>{msg.timestamp.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>
                {msg.role === 'assistant' && msg.activeCategoryName && (
                  <span className="text-gray-400">
                    etiqueta: <span className="text-white">{msg.activeCategoryName}</span>
                  </span>
                )}
                {msg.role === 'assistant' && msg.debug && (
                  <span className="text-gray-700">
                    {msg.debug.model}
                  </span>
                )}
              </div>

              {/* Debug info for assistant messages */}
              {showDebug && msg.role === 'assistant' && (
                <div className="mt-2 space-y-2">
                  {/* Stages for this message */}
                  {msg.stages && msg.stages.length > 0 && (
                    <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-3">
                      <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-2">Pipeline Stages</p>
                      <div className="space-y-1">
                        {compactStages(msg.stages).map((stage, si) => (
                          <div key={si} className="flex items-center gap-2 text-xs">
                            <StageIcon status={stage.status} />
                            <span className={`font-mono w-28 flex-shrink-0 ${stage.status === 'error' ? 'text-red-400' : 'text-gray-400'}`}>
                              {STAGE_LABELS[stage.name] || stage.name}
                            </span>
                            <span className="text-gray-600 truncate flex-1">{stage.detail}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Labeler output */}
                  {msg.labeler && (
                    <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-3">
                      <p className="text-[10px] font-medium text-white uppercase tracking-wider mb-1">Agente Etiquetador</p>
                      <pre className="text-xs text-gray-300 whitespace-pre-wrap font-mono">{msg.labeler.output}</pre>
                    </div>
                  )}

                  {msg.debug?.promptText && (
                    <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-3">
                      <div className="flex items-center justify-between gap-3 mb-2">
                        <p className="text-[10px] font-medium text-white uppercase tracking-wider">Prompt usado na resposta</p>
                        <div className="flex items-center gap-2 text-[10px] text-gray-400">
                          <span>origem: <span className="text-gray-200">{getPromptSourceLabel(msg.debug?.promptSource)}</span></span>
                          {msg.debug?.promptCategoryName && (
                            <span>categoria: <span className="text-white">{msg.debug.promptCategoryName}</span></span>
                          )}
                        </div>
                      </div>
                      <pre className="text-xs text-gray-300 whitespace-pre-wrap font-mono max-h-64 overflow-y-auto">{msg.debug.promptText}</pre>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}

        {/* Processing indicator */}
        {isProcessing && (
          <div className="flex justify-start">
            <div className="bg-gray-800 border border-gray-700 rounded-2xl rounded-bl-md px-4 py-3">
              <div className="flex items-center gap-2">
                <div className="flex gap-1">
                  <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
                <span className="text-xs text-gray-500">Agente processando...</span>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Session info bar */}
      {sessionInfo && (
        <div className="flex-shrink-0 px-4 py-1.5 border-t border-gray-800/50 bg-gray-900/30 flex items-center gap-4 text-[10px] text-gray-500 overflow-x-auto">
          <span>Sessao: <span className="text-gray-400 font-mono">{sessionInfo.id?.substring(0, 16)}...</span></span>
          <span>Msgs: <span className="text-gray-400">{sessionInfo.messageCount}</span></span>
          {sessionInfo.currentCategory && (
            <span>Etiqueta: <span className="text-white font-medium">{sessionInfo.currentCategory}</span></span>
          )}
          {sessionInfo.observations && (
            <span className="truncate max-w-sm" title={sessionInfo.observations}>
              Obs: <span className="text-gray-400">{sessionInfo.observations.substring(0, 80)}{sessionInfo.observations.length > 80 ? '...' : ''}</span>
            </span>
          )}
        </div>
      )}

      {/* Input area */}
      <div className="flex-shrink-0 border-t border-gray-800 bg-gray-900/80 backdrop-blur-sm p-4">
        <div className="flex items-end gap-3">
          <div className="flex-1 relative">
            <textarea
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Digite sua mensagem... (Enter para enviar, Shift+Enter para nova linha)"
              disabled={isProcessing}
              rows={1}
              className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-gray-500 resize-none transition-colors disabled:opacity-50"
              style={{ minHeight: '44px', maxHeight: '120px' }}
              onInput={e => {
                const target = e.target as HTMLTextAreaElement;
                target.style.height = 'auto';
                target.style.height = Math.min(target.scrollHeight, 120) + 'px';
              }}
            />
          </div>
          <button
            onClick={handleSendAsAssistant}
            disabled={!input.trim() || isProcessing}
            className="flex-shrink-0 px-3 h-11 rounded-xl bg-gray-800 hover:bg-gray-700 disabled:bg-gray-800 disabled:cursor-not-allowed border border-gray-700 text-xs text-gray-200 transition-colors"
          >
            IA
          </button>
          <button
            onClick={handleSend}
            disabled={!input.trim() || isProcessing}
            className="flex-shrink-0 w-11 h-11 rounded-xl bg-gray-700 hover:bg-gray-600 disabled:bg-gray-700 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
          >
            {isProcessing ? (
              <svg className="w-5 h-5 text-white animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            ) : (
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
